/// Tooltip: Wraps Radix UI Tooltip primitive with consistent dark styling.

import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import type { ReactNode } from "react";

interface TooltipProps {
  /** The element that triggers the tooltip on hover. */
  children: ReactNode;
  /** Text content of the tooltip. */
  content: string;
  /** Side of the trigger to show the tooltip. */
  side?: "top" | "right" | "bottom" | "left";
  /** Delay before showing in ms. */
  delayDuration?: number;
}

/**
 * Consistent dark-themed tooltip using Radix UI.
 * Wraps children in a Tooltip trigger and renders
 * a styled content popup on hover.
 */
export function Tooltip({ children, content, side = "bottom", delayDuration = 300 }: TooltipProps) {
  if (!content) {
    return <>{children}</>;
  }

  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            sideOffset={4}
            className="z-50 rounded bg-zinc-950 px-2 py-1 text-xs text-zinc-200 shadow-lg border border-zinc-800 animate-in fade-in-0 zoom-in-95"
          >
            {content}
            <TooltipPrimitive.Arrow className="fill-zinc-950" />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
